// Ekran 04 — Cariler (Firma Yönetimi).
// Yerleşim, kolonlar ve sağ panel ref_files/btys-erp-04-cariler.html'den birebir.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { useSekmeler } from '../../components/Sekmeler'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { sayiyaCevirBos, tr, trGiris, trTarih } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import { cariler, type Cari, type CariOzet } from './api'
import CariAnaliziSekmesi from './CariAnaliziSekmesi'
import {
  ROLLER,
  RolAnahtarlari,
  RolRozetleri,
  sapmaMetni,
  sapmaSinifi,
  sapmaVurgusu,
  type RolAnahtar,
} from './CariOrtak'
import Sayfalayici from '../../components/Sayfalayici'
import IceAktarDrawer from '../../components/IceAktarDrawer'

const SEGMENTLER: { anahtar: string; etiket: string; alan?: RolAnahtar }[] = [
  { anahtar: '', etiket: 'Tümü' },
  { anahtar: 'atik', etiket: 'Atık Sağlayıcı', alan: 'rolAtik' },
  { anahtar: 'tedarikci', etiket: 'Tedarikçi', alan: 'rolTedarikci' },
  { anahtar: 'nakliyeci', etiket: 'Nakliyeci', alan: 'rolNakliyeci' },
]

const SAYFA_BOYU = 25

function Icerik() {
  const [rol, setRol] = useState('')
  const [arama, setArama] = useState('')
  const [sayfa, setSayfa] = useState(1)
  const [seciliId, setSeciliId] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const kuyruk = useQueryClient()
  const { ac } = useDrawer()
  const sekmeler = useSekmeler()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('FiloCariler', 'yaz')

  /** Referans `openFull()` — satır çift tıkı, satır ↗ ve panel altlığı buraya bağlı. */
  function analizAc(c: CariOzet) {
    sekmeler.ac({ id: `c-${c.kod}`, baslik: c.unvan, icerik: <CariAnaliziSekmesi id={c.id} /> })
  }

  const { data: ozet } = useQuery({ queryKey: ['cari-ozeti'], queryFn: cariler.ozet })

  // Segment sayaçları TÜM listeden hesaplanır; filtreli listeden saymak her
  // segmentte kendi sayısını gösterirdi.
  const { data: tumu = [] } = useQuery({
    queryKey: ['cariler', '', ''],
    queryFn: () => cariler.liste(),
  })

  const { data: liste = [], isLoading } = useQuery({
    queryKey: ['cariler', rol, arama],
    queryFn: () => cariler.liste(arama || undefined, rol || undefined),
  })

  const { data: detay } = useQuery({
    queryKey: ['cari', seciliId],
    queryFn: () => cariler.detay(seciliId!),
    enabled: !!seciliId,
  })

  /**
   * "Bilgiler — hızlı düzenle" bloğunun yerel taslağı.
   *
   * Alanlar referanstan `defaultValue` ile taşınmıştı ve Kaydet düğmesi YALNIZ
   * toast basıyordu; kullanıcı yetkili/telefon/vade/adres değiştirip
   * "kaydedildi" görüyor, hiçbir şey yazılmıyordu.
   */
  const [bilgi, setBilgi] = useState({ yetkili: '', tel: '', vadeGun: '', adres: '' })

  useEffect(() => {
    if (!detay) return
    setBilgi({
      yetkili: detay.cari.yetkili ?? '',
      tel: detay.cari.tel ?? '',
      vadeGun: trGiris(detay.cari.vadeGun),
      adres: detay.cari.adres ?? '',
    })
  }, [detay])

  const sayfaSayisi = Math.max(1, Math.ceil(liste.length / SAYFA_BOYU))
  const gecerliSayfa = Math.min(sayfa, sayfaSayisi)
  const sayfadakiler = liste.slice((gecerliSayfa - 1) * SAYFA_BOYU, gecerliSayfa * SAYFA_BOYU)

  // Referans açılışta bir firmayı seçili getiriyor; panel boş kutu olarak durmasın.
  const ilkId = sayfadakiler[0]?.id
  useEffect(() => {
    if (!seciliId && ilkId) setSeciliId(ilkId)
  }, [seciliId, ilkId])

  const sayac = (s: (typeof SEGMENTLER)[number]) =>
    s.alan ? tumu.filter((c) => c[s.alan!]).length : tumu.length

  const kaydet = useMutation({
    mutationFn: (govde: Partial<Cari>) => cariler.kaydet(seciliId, govde),
    onSuccess: (c) => {
      toast(`${c.kod} · ${c.unvan} kaydedildi`)
      void kuyruk.invalidateQueries({ queryKey: ['cari', seciliId] })
      void kuyruk.invalidateQueries({ queryKey: ['cariler'] })
      void kuyruk.invalidateQueries({ queryKey: ['cari-ozeti'] })
    },
    onError: (e) => toast(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.'),
  })

  function rolDegistir(anahtar: RolAnahtar) {
    if (!detay || !yazabilir) return
    const c = detay.cari
    // "En az bir rol" kuralını sunucu 409 ile zorluyor; kullanıcıyı boşuna
    // sunucuya göndermemek için son rolü burada da tutuyoruz (referans da böyle).
    const acikSayisi = ROLLER.filter((r) => c[r.anahtar]).length
    if (c[anahtar] && acikSayisi === 1) {
      toast('Firma en az bir rol taşımalı')
      return
    }
    kaydet.mutate({
      kod: c.kod, unvan: c.unvan, tel: c.tel, eposta: c.eposta, adres: c.adres,
      vergiNo: c.vergiNo, yetkili: c.yetkili, vadeGun: c.vadeGun, durum: c.durum,
      rolAtik: c.rolAtik, rolTedarikci: c.rolTedarikci, rolNakliyeci: c.rolNakliyeci,
      [anahtar]: !c[anahtar],
    })
  }

  return (
    <>
      <div className="crumb">
        Ana Veri <b>/ Cariler</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Cariler — Firma Yönetimi</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Faz 7 kapsamı dışında — dışa aktarma henüz bağlanmadı"
            onClick={() => undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Dışa aktar
          </button>
          <button
            className="btn btn-sec"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? 'Excel şablonuyla toplu yükle' : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Carileri içe aktar',
                altBaslik: 'Excel şablonuyla toplu yükleme',
                icerik: (
                  <IceAktarDrawer
                    baslik="cari"
                    sablonYolu="/api/cariler/sablon"
                    sablonAdi="btys-cari-sablonu.xlsx"
                    iceAktarYolu="/api/cariler/ice-aktar"
                    tazeleAnahtari={['cariler', 'cari-ozeti']}
                  />
                ),
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            İçe aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Yeni firma',
                altBaslik: 'Roller birlikte seçilebilir — sonradan da değiştirilebilir',
                boyut: 'sm',
                icerik: <YeniCariDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Yeni firma
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Toplam Firma</div>
          <div className="k-v">{ozet?.toplamFirma ?? '—'}</div>
          <div className="k-s">aktif cari kaydı</div>
        </div>
        <div className="k">
          <div className="k-l">Atık Sağlayıcı</div>
          <div className="k-v">{ozet?.atikSaglayici ?? '—'}</div>
          <div className="k-s">hammadde kaynağı tesis</div>
        </div>
        <div className="k">
          <div className="k-l">Tedarikçi</div>
          <div className="k-v">{ozet?.tedarikci ?? '—'}</div>
          <div className="k-s">sarf / malzeme / yakıt</div>
        </div>
        <div className="k">
          <div className="k-l">Nakliyeci</div>
          <div className="k-v">{ozet?.nakliyeci ?? '—'}</div>
          <div className="k-s">taşıma operasyonu</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Hammadde</div>
          <div className="k-v">{ozet?.buAyHammaddeT ?? '—'}</div>
          <div className="k-s">ton · mal kabul Faz 2’de</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map((s) => (
                <button
                  key={s.anahtar}
                  type="button"
                  className={rol === s.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => {
                    setRol(s.anahtar)
                    setSayfa(1)
                  }}
                >
                  {s.etiket}
                  <small>{sayac(s)}</small>
                </button>
              ))}
            </div>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Kod, unvan, VKN, il ara…"
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value)
                  setSayfa(1)
                }}
              />
            </div>
            <div className="toolbar-right">
              <span>{liste.length} firma</span>
            </div>
          </div>

          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 76 }}>Kod</th>
                <th>Firma</th>
                <th style={{ width: 86 }}>Roller</th>
                <th style={{ width: 120 }}>Bu Ay</th>
                <th className="num" style={{ width: 96 }}>KM Sapma %</th>
                <th style={{ width: 82 }}>Son İşlem</th>
                <th className="num" style={{ width: 96 }}>Bakiye ₺</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
              )}
              {!isLoading && liste.length === 0 && (
                <tr><td colSpan={8} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
              )}
              {sayfadakiler.map((c) => (
                <tr
                  key={c.id}
                  className={[
                    seciliId === c.id ? 'sel' : '',
                    c.durum === 'pasif' ? 'pasif' : '',
                  ].filter(Boolean).join(' ')}
                  title={c.durum === 'pasif' ? 'Pasif kayıt' : undefined}
                  onClick={() => setSeciliId(c.id)}
                  onDoubleClick={() => analizAc(c)}
                >
                  <td><span className="kod">{c.kod}</span></td>
                  <td>
                    <div className="cell-name">
                      {c.unvan}
                      {c.analizsizHavuzVar && (
                        <span className="warn-i" title="Analizsiz teslimat var">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="cell-sub">
                      {[c.vergiNo, c.adres].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td><RolRozetleri c={c} /></td>
                  <td className="mono" style={{ fontSize: 11.5 }}>{c.buAy ?? '—'}</td>
                  <td className={`num ${sapmaSinifi(c.kmSapmaOrt)}`}>{sapmaMetni(c.kmSapmaOrt)}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{c.sonIslem ?? '—'}</td>
                  <td className="num">{c.bakiye ? tr(c.bakiye, 0) : '—'}</td>
                  <td>
                    <div className="row-acts">
                      <button
                        className="mini-btn"
                        type="button"
                        title="Firma analizi (tam sayfa)"
                        onClick={(e) => {
                          e.stopPropagation()
                          analizAc(c)
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="grid-foot">
            <span>Toplam {tumu.length} cari · bir firma birden fazla rol taşıyabilir</span>
            <Sayfalayici gecerli={gecerliSayfa} toplam={sayfaSayisi} git={setSayfa} />
          </div>
        </div>

        <aside className="insp card" ref={panel}>
          {!detay ? (
            <div className="insp-empty">Bir firma seçin — 360° özet burada.</div>
          ) : (
            <>
              <div className="insp-h">
                <div className="insp-eyebrow">
                  <span className="kod">{detay.cari.kod}</span>
                  {detay.cari.vergiNo && (<><span>·</span><span>{detay.cari.vergiNo}</span></>)}
                  {detay.cari.adres && (<><span>·</span><span>{detay.cari.adres}</span></>)}
                </div>
                <div className="insp-t">
                  {detay.cari.unvan} <RolRozetleri c={detay.cari} />{' '}
                  {detay.cari.durum === 'pasif' && <span className="badge b-gray">Pasif</span>}
                </div>
              </div>

              <div className="insp-b">
                <div className="insp-sec">
                  <div className="insp-sec-t">Roller — birden fazla seçilebilir</div>
                  <RolAnahtarlari
                    c={detay.cari}
                    kilitli={!yazabilir || kaydet.isPending}
                    kilitNedeni={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
                    degistir={rolDegistir}
                  />
                </div>

                <div className="insp-sec">
                  <div className="insp-sec-t">Bilgiler — hızlı düzenle</div>
                  <div className="fgrid">
                    <div className="fld">
                      <label>Yetkili</label>
                      <input
                        className="in"
                        value={bilgi.yetkili}
                        readOnly={!yazabilir}
                        onChange={(e) => setBilgi((b) => ({ ...b, yetkili: e.target.value }))}
                      />
                    </div>
                    <div className="fld">
                      <label>Telefon</label>
                      <input
                        className="in mono"
                        value={bilgi.tel}
                        readOnly={!yazabilir}
                        onChange={(e) => setBilgi((b) => ({ ...b, tel: e.target.value }))}
                      />
                    </div>
                    <div className="fld">
                      <label>Ödeme vadesi (gün)</label>
                      <input
                        className="in mono"
                        value={bilgi.vadeGun}
                        readOnly={!yazabilir}
                        onChange={(e) => setBilgi((b) => ({ ...b, vadeGun: e.target.value }))}
                      />
                    </div>
                    <div className="fld">
                      <label>İl / İlçe</label>
                      <input
                        className="in"
                        value={bilgi.adres}
                        readOnly={!yazabilir}
                        onChange={(e) => setBilgi((b) => ({ ...b, adres: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Rol bazlı bloklar — referans yalnız ilgili rol açıksa basıyor. */}
                {detay.cari.rolAtik && (
                  <div className="insp-sec">
                    <div className="inst">
                      <div className="inst-t">Hammadde Karnesi — Bu Ay</div>
                      <div className="inst-3">
                        <div className="inst-c">
                          <div className="inst-v">—</div>
                          <div className="inst-l">ton teslimat</div>
                        </div>
                        <div className="inst-c">
                          <div className="inst-v">—</div>
                          <div className="inst-l">kabul</div>
                        </div>
                        <div className="inst-c">
                          <div className={`inst-v ${sapmaVurgusu(detay.kmSapmaOrt)}`}>
                            {sapmaMetni(detay.kmSapmaOrt)}%
                          </div>
                          <div className="inst-l">KM sapması</div>
                        </div>
                      </div>
                      <div className="inst-note">
                        Sapma = analiz ort. ↔ stok kartı (tonaj ağırlıklı) · teslimat
                        sayıları mal kabulden (Faz 2) gelecek
                      </div>
                    </div>
                  </div>
                )}

                {detay.cari.rolTedarikci && (
                  <div className="insp-sec">
                    <div className="insp-sec-t">Tedarik — Bu Ay</div>
                    <div className="mini-list">
                      <div className="mini-li">
                        Satın alma kalemleri<span className="vv">Faz 2</span>
                      </div>
                    </div>
                  </div>
                )}

                {detay.cari.rolNakliyeci && (
                  <div className="insp-sec">
                    <div className="insp-sec-t">Nakliye — Bu Ay</div>
                    <div className="mini-list">
                      <div className="mini-li">Sefer<span className="vv">—</span></div>
                      <div className="mini-li">Taşınan tonaj<span className="vv">—</span></div>
                      <div className="mini-li">Araç<span className="vv">—</span></div>
                    </div>
                  </div>
                )}

                <div className="insp-sec">
                  <div className="insp-sec-t">Cari Ekstre (K12)</div>
                  <div className="mini-list">
                    <div className="mini-li">
                      Bakiye — firma alacaklı<span className="vv">—</span>
                    </div>
                    {/* Referans son iki işlemi listeliyor; kaynak defterin bu
                        firmaya havuz üzerinden bağlanan satırları (K26). */}
                    {detay.islemler.length === 0 ? (
                      <div className="mini-li">
                        <span className="cell-sub">Defterde bu firmaya bağlı hareket yok</span>
                      </div>
                    ) : (
                      detay.islemler.slice(0, 2).map((i) => (
                        <div className="mini-li" key={`${i.referans}-${i.zaman}-${i.detay}`}>
                          <span className="dt">{trTarih(i.zaman)}</span>
                          {i.tip} · {i.referans}
                          <span className="vv">
                            {i.yon < 0 ? '−' : i.yon > 0 ? '+' : ''}
                            {tr(i.miktar, 1)} {i.birim}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="insp-f">
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  title="Firma 360° analiz"
                  style={{ padding: '0 8px' }}
                  onClick={() => analizAc(detay.cari)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                  </svg>
                </button>
                <button
                  className="btn btn-pri btn-sm"
                  type="button"
                  style={{ flex: 1, justifyContent: 'center' }}
                  disabled={!yazabilir || kaydet.isPending}
                  title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
                  onClick={() =>
                    kaydet.mutate({
                      // Sunucu tüm alanları bekliyor; mevcut carinin üzerine
                      // yalnız "hızlı düzenle" bloğundakiler yazılıyor.
                      ...detay.cari,
                      yetkili: bilgi.yetkili.trim() || null,
                      tel: bilgi.tel.trim() || null,
                      adres: bilgi.adres.trim() || null,
                      vadeGun: sayiyaCevirBos(bilgi.vadeGun),
                    })
                  }
                >
                  Kaydet
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  )
}

// ------------------------------------------------------------ yeni cari

function YeniCariDrawer() {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [kod, setKod] = useState('')
  const [unvan, setUnvan] = useState('')
  const [vkn, setVkn] = useState('')
  const [il, setIl] = useState('')
  const [yetkili, setYetkili] = useState('')
  const [vadeGun, setVadeGun] = useState('')
  const [roller, setRoller] = useState<Set<RolAnahtar>>(new Set(['rolAtik']))
  const [bekliyor, setBekliyor] = useState(false)

  const gecerli = kod.trim().length > 0 && unvan.trim().length > 0 && roller.size > 0

  /** Referans `tRol()` — son açık rol kapatılamaz; sunucu da 409 döndürüyor. */
  function rolDegistir(anahtar: RolAnahtar) {
    if (!roller.has(anahtar)) {
      setRoller(new Set(roller).add(anahtar))
      return
    }
    // Toast state güncelleyicisinin İÇİNDE değil: updater StrictMode'da iki kez
    // çalışıyor ve mesaj çiftleniyordu.
    if (roller.size === 1) {
      toast('Firma en az bir rol taşımalı')
      return
    }
    const y = new Set(roller)
    y.delete(anahtar)
    setRoller(y)
  }

  async function kaydet() {
    if (!gecerli || bekliyor) return
    setBekliyor(true)
    try {
      const c = await cariler.kaydet(null, {
        kod: kod.trim(), unvan: unvan.trim(), vergiNo: vkn || null, adres: il || null,
        yetkili: yetkili.trim() || null, vadeGun: sayiyaCevirBos(vadeGun),
        rolAtik: roller.has('rolAtik'),
        rolTedarikci: roller.has('rolTedarikci'),
        rolNakliyeci: roller.has('rolNakliyeci'),
        durum: 'aktif',
      })
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['cariler'] }),
        kuyruk.invalidateQueries({ queryKey: ['cari-ozeti'] }),
      ])
      kapat()
      toast(`${c.kod} · ${c.unvan} eklendi`)
    } catch (e) {
      toast(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        {/* Referans sırası: önce roller (chip), sonra kimlik alanları (ref:631-639). */}
        <div className="fld" style={{ marginBottom: 10 }}>
          <label>Roller</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {ROLLER.map((r) => (
              <button
                key={r.anahtar}
                type="button"
                className={roller.has(r.anahtar) ? 'rolchip on' : 'rolchip'}
                aria-pressed={roller.has(r.anahtar)}
                onClick={() => rolDegistir(r.anahtar)}
              >
                {r.etiket}
              </button>
            ))}
          </div>
        </div>
        <div className="fgrid">
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Unvan</label>
            <input className="in" value={unvan} onChange={(e) => setUnvan(e.target.value)} placeholder="Örn. Anadolu Kimya A.Ş." />
          </div>
          <div className="fld">
            <label>VKN</label>
            <input className="in mono" value={vkn} onChange={(e) => setVkn(e.target.value)} />
          </div>
          <div className="fld">
            <label>İl / İlçe</label>
            <input className="in" value={il} onChange={(e) => setIl(e.target.value)} />
          </div>
          <div className="fld">
            <label>Yetkili</label>
            <input className="in" value={yetkili} onChange={(e) => setYetkili(e.target.value)} />
          </div>
          <div className="fld">
            <label>Ödeme vadesi (gün)</label>
            <input className="in mono" value={vadeGun} onChange={(e) => setVadeGun(e.target.value)} />
          </div>
          {/* Referansta kod alanı yok — `saveYeni()` kodu kendi üretiyor. Sunucuda
              öyle bir üreteç olmadığı ve Kod zorunlu olduğu için alan formda kalıyor. */}
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Kod</label>
            <input className="in mono" value={kod} onChange={(e) => setKod(e.target.value)} placeholder="CR-0015" />
            <div className="fld-hint">Kod tekildir; otomatik üretim henüz yok</div>
          </div>
        </div>
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>Vazgeç</button>
        <button className="btn btn-pri" type="button" disabled={!gecerli || bekliyor} onClick={() => void kaydet()}>
          {bekliyor ? 'Kaydediliyor…' : 'Firmayı kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}

export default function CarilerSayfasi() {
  return (
    <Kabuk listeBasligi="Cariler">
      <YetkiKapisi modul="FiloCariler">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
